import { onMounted, onUnmounted, getCurrentInstance } from "vue";

export interface BuildCheckOptions {
  /** Where the backend exposes the current build date */
  url?: string; // default: "/build.json"
  /** How often (ms) the check should run */
  interval?: number; // default: 60 000 ms
}

/**
 * Periodically compares the deployed build date against the one that was baked into the currently running bundle.
 * When they differ, the page is reloaded so the user gets the fresh assets.
 */

export function useBuildCheck({
  url = "/build.json",
  interval = 180000
}: BuildCheckOptions = {}) {
  const currentBuild = import.meta.env.VITE_APP_BUILD as string | undefined;

  let buildCheckTimer: ReturnType<typeof setInterval> | undefined;

  async function checkBuild() {
    // Don’t attempt anything if we don’t know the current build
    if (!currentBuild) return;

    try {
      const response = await fetch(url, { cache: "no-store" });

      // Treat HTTP errors as a failed check
      if (!response.ok) {
        throw new Error(
          `Error retrieving build, ${response.status} ${response.statusText}`
        );
      }

      const data: unknown = await response.json();

      if (
        typeof data === "object" &&
        data !== null &&
        "build" in data &&
        typeof (data as Record<string, unknown>).build === "string"
      ) {
        const { build } = data as { build: string };

        if (build !== currentBuild) {
          // Force refresh
          // @TODO - ask the user if they want to reload
          window.location.reload();
        }
      } else {
        throw new Error("Error retrieving build, Unexpected data shape");
      }
    } catch (error) {
      console.error("Build check failed, ", error);
    }
  }

  // Only register lifecycle hooks when we are inside a component
  if (getCurrentInstance()) {
    onMounted(() => {
      void checkBuild();
      buildCheckTimer = setInterval(checkBuild, interval);
    });
    onUnmounted(stop);
  } else {
    // Fallback (e.g. called from plain JS): start immediately
    buildCheckTimer = setInterval(checkBuild, interval);
  }

  /** Stops the periodic polling. */
  function stop() {
    if (buildCheckTimer !== undefined) {
      clearInterval(buildCheckTimer);
      buildCheckTimer = undefined;
    }
  }

  return { stop };
}
